import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'lodash';
import { from } from 'rxjs';
import { CompanyVendor, CompanyVendorFile, User } from 'src/app/classes/models';
import { File as FileModel } from 'src/app/classes/models/file';
import { CompanyVendorFileType } from 'src/app/interfaces/company-vendor-file-type';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-company-vendor-file-upload-modal',
  templateUrl: './company-vendor-file-upload-modal.component.html',
  styleUrl: './company-vendor-file-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CompanyVendorFileUploadModalComponent {
  constructor(
    private activeModal: NgbActiveModal,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private CompanyVendorService: CompanyVendorService,
    private cdr: ChangeDetectorRef
  ) {
    this.LoggedInUser = this.AuthService.getUser();
    const { UserAccountRole } = this.LoggedInUser;

    if (UserAccountRole) {
      const { role } = UserAccountRole;

      if (!role) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing user permission.',
          { returnError: true }
        );
      }

      const all: CompanyVendorFileType[] = [
        'Board Resolution',
        'Promotions',
        'Secretary of State',
        'Work Comp Exemption',

        'Other'
      ];
      const clientAdmins: CompanyVendorFileType[] = [
        'Board Resolution',
        'Promotions',
        'W9',
        'Secretary of State',
        'Work Comp Exemption',
        'Other'
      ];
      const admins: CompanyVendorFileType[] = all.concat(['Compliance Waiver']);
      const viveAdmins: CompanyVendorFileType[] = admins.concat(
        'LexisNexis',
        'Public Data-Business',
        '3rd Party Business',
        'Public Data-Owner',
        '3rd Party Owner',
        'Manual Packet',
        'Important Note'
      );

      if (
        new Set([
          'ADMIN_ADMIN',
          'ADMIN_EMAILS_TEAM',
          'ADMIN_INSURANCE_TEAM',
          'ADMIN_FRAUD_TEAM',
          'ADMIN_CUSTOMER_SERVICE_TEAM',
          'ADMIN_PROJECT_MANAGEMENT_TEAM'
        ]).has(role)
      ) {
        this.companyVendorFileDocTypes = viveAdmins;
      } else if (role === 'CLIENT_ADMIN') {
        this.companyVendorFileDocTypes = clientAdmins;
      } else {
        this.companyVendorFileDocTypes = all;
      }
    }
  }

  @Input() title?: string;
  @Input() CompanyVendor?: CompanyVendor;

  Files: FileModel[] = [];
  LoggedInUser!: User;
  companyVendorFileDocTypes: string[] = [];
  companyVendorFilePermissions: string[] = [];
  chosenCompanyVendorFileDocType?: CompanyVendorFileType;
  chosenVendorFilePermission?: string;
  allVendorClients?: boolean = false;
  companyVendorFilesToSave: CompanyVendorFile[] = [];

  isAdminUser() {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user account role.',
        { returnError: true }
      );
    }

    return new Set([
      'ADMIN_ADMIN',
      'ADMIN_PRIME_TEAM',
      'ADMIN_EMAILS_TEAM',
      'ADMIN_INSURANCE_TEAM',
      'ADMIN_FRAUD_TEAM',
      'ADMIN_CUSTOMER_SERVICE_TEAM',
      'ADMIN_PROJECT_MANAGEMENT_TEAM'
    ]).has(role);
  }

  removeFile({ index }: { index: number }) {
    this.Files.splice(index, 1);
    this.cdr.markForCheck();
  }

  fileUpload({ data: File }: { data: File }) {
    const { name: filename, size: filesize, type: filetype } = File;

    from(File.arrayBuffer()).subscribe({
      next: (FileArrayBuffer) => {
        const FileString = `data:${filetype};base64,${this.UtilityService.convertArrayBufferToBase64EncodedString(FileArrayBuffer)}`;

        const uploadedFile = {
          filename,
          filesize,
          filetype,
          FileContent: {
            filecontents: FileString
          }
        };

        this.Files.push(uploadedFile);
        this.cdr.markForCheck();
      }
    });
  }

  onAllVendorClientsChange() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload file(s). No Company vendor record found',
        { returnError: true }
      );
    }

    const { VendorId, CompanyId } = this.CompanyVendor;

    if (this.allVendorClients) {
      this.CompanyVendorService.getCompanyVendors(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: ['id', 'VendorId', 'CompanyId'],
          VendorId,
          CompanyId: { $ne: CompanyId }
        })
      ).subscribe({
        next: (CompanyVendors: CompanyVendor[]) => {
          CompanyVendors.forEach((cv) => {
            this.createCompanyVendorFile(cv);
          });
          this.cdr.markForCheck();
        }
      });
    } else {
      this.companyVendorFilesToSave = this.companyVendorFilesToSave.filter(
        (file) => file.CompanyId === CompanyId
      );
      this.cdr.markForCheck();
    }
  }

  onFileTypeChange() {
    this.calculatePermission();
    this.cdr.markForCheck();
  }

  calculatePermission() {
    const permissionLevel: Map<CompanyVendorFileType, string[]> = new Map([
      ['Board Resolution', ['CLIENT', 'ADMIN']],
      ['Promotions', ['ALL']],
      ['W9', ['CLIENT_ADMIN', 'ADMIN']],
      ['Secretary of State', ['CLIENT_ADMIN', 'ADMIN']],
      ['LexisNexis', ['ADMIN']],
      ['Public Data-Business', ['ADMIN']],
      ['3rd Party Business', ['ADMIN']],
      ['Public Data-Owner', ['ADMIN']],
      ['3rd Party Owner', ['ADMIN']],
      ['Compliance Waiver', ['CLIENT_ADMIN']],
      ['Work Comp Exemption', ['ALL']],
      ['Manual Packet', ['ADMIN']],
      [
        'Other',
        ['ALL', 'ADMIN', 'CLIENT', 'VENDOR', 'VENDOR_ADMIN', 'CLIENT_ADMIN']
      ],
      ['Important Note', ['ADMIN']]
    ]);

    const { UserAccountRoleId, UserAccountTypeId } = this.LoggedInUser;

    let permissionSelection =
      permissionLevel.get(this.chosenCompanyVendorFileDocType!) || [];
    if (UserAccountTypeId === 3 && UserAccountRoleId !== 1) {
      permissionSelection = filter(
        permissionSelection,
        (perm) => perm === 'CLIENT' || perm === 'ALL'
      );
    }
    if (UserAccountTypeId === 3 && UserAccountRoleId === 1) {
      permissionSelection = filter(
        permissionSelection,
        (perm) => perm === 'CLIENT_ADMIN' || perm === 'CLIENT' || perm === 'ALL'
      );
    }

    this.companyVendorFilePermissions = permissionSelection;
    this.cdr.markForCheck();
  }

  createCompanyVendorFile(companyVendor: CompanyVendor) {
    const {
      id: CompanyVendorId,
      CompanyId,
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId
    } = companyVendor;
    const companyVendorFile = new CompanyVendorFile({});
    const File = this.Files[0];

    if (
      !CompanyVendorId ||
      !CompanyId ||
      (!VendorId && !ExemptVendorId && !ClientRegisteredVendorId)
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload companyVendor file. Missing required info.',
        { returnError: true }
      );
    }

    if (
      this.isAdminUser() &&
      (!this.chosenCompanyVendorFileDocType || !this.chosenVendorFilePermission)
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload companyVendor file. Missing required type or permission level.',
        { returnError: true }
      );
    }

    Object.assign(companyVendorFile, {
      CompanyVendorId,
      CompanyId,
      VendorId,
      ExemptVendorId,
      ClientRegisteredVendorId,
      docType: this.chosenCompanyVendorFileDocType,
      permission: this.chosenVendorFilePermission,

      File
    });

    this.companyVendorFilesToSave.push(companyVendorFile);
  }

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  upload() {
    if (!this.Files || this.Files.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload file(s). No files found to upload.',
        { returnError: true }
      );
    }

    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload file(s). No Company vendor record found',
        { returnError: true }
      );
    }

    if (this.Files.length > 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Can only upload 1 CompanyVendor File at a time.',
        { returnError: true }
      );
    }

    this.createCompanyVendorFile(this.CompanyVendor);

    this.activeModal.close(this.companyVendorFilesToSave);
  }
}
